/**
 * Helpers Droit Public — bascule contextuelle entre vocabulaire civil et
 * vocabulaire administratif sans toucher au schéma de données.
 *
 * Marqueur de matière : `isPublicLawProcedureType()` couvre les libellés
 * administratifs actuels et historiques sans ajouter de boolean dédié.
 *
 * Surfaces consommatrices (suivantes) :
 *   - `CaseView` → renomme « Mes écritures » → « Mes mémoires », « Pièces
 *     adverses » → « Productions du défendeur »
 *   - Les décisions attaquées ne créent plus de sous-dossier : leur tag est
 *     informatif, la sélection pour une génération étant explicite.
 *   - `BordereauPanel` → débloque les modes Télérecours (productions /
 *     récapitulatif) en plus du mode communication classique
 *   - `piece-ingestion` (backend) → bascule le prompt de suggestion de nom
 *     sur le dictionnaire admin
 *   - `Case Wiki` → rend le vocabulaire des rôles de parties adapté à la
 *     matière : « Requérant » au lieu de « Notre client » (côté requérant),
 *     « Administration » au lieu de « Adverse/Défendeur » (côté administration
 *     défenderesse) dans les dashboards Parties, Aperçu, Faits, Chronologie,
 *     Procédure et le graphe.
 *
 * Le label "Pièces" reste identique en civil et en admin — c'est la même
 * notion juridique (productions documentaires). Seules « écritures » et
 * « pièces adverses » divergent.
 */

import { isPublicLawProcedureType } from '@hector/shared-types';

import { DOCUMENT_CATEGORIES, type DocumentCategoryKey } from '@/data/documentCategories';
import type { Case } from '@/types/api';

export type DroitPublicSection = 'mes_ecritures' | 'pieces_adverses' | 'nos_pieces';

/**
 * Rôles de parties exposés dans le Case Wiki. Un seul axe ici :
 * - `client`   → la partie qu'on représente (civil : « Notre client » ;
 *                admin : « Requérant »)
 * - `opponent` → la partie adverse (civil : « Partie adverse » ;
 *                admin : « Administration »)
 * - `clients`  → pluriel du camp client (civil : « Nos clients » ;
 *                admin : « Requérants »)
 * - `opponents`→ pluriel du camp adverse (civil : « Adverses » ;
 *                admin : « Administration »)
 *
 * Note : « Administration » est préféré à « Partie adverse » en admin
 * car le jargon administratif désigne invariablement la personne publique
 * défenderesse comme « l'Administration ». « Adversaire » / « Partie adverse »
 * s'emploient en civil mais sonnent faux en contentieux administratif où
 * l'identité institutionnelle prime (décision — Gérald Arbant,
 * avocat barreau Paris, review métier 2026-06-21).
 */
export type PartyRole = 'client' | 'opponent' | 'clients' | 'opponents';

/**
 * Les libellés administratifs courants et historiques sont résolus par le
 * matcher partagé Case Wiki, unique source de vérité pour cette normalisation.
 */
/**
 * Marqueur de matière administrative. Match les 3 étiquettes canoniques de
 * `procedure_type` sans dupliquer la normalisation partagée.
 */
export function isDroitPublic(c: Pick<Case, 'procedure_type'> | null | undefined): boolean {
  return isPublicLawProcedureType(c?.procedure_type);
}

const LABELS_CIVIL: Record<DroitPublicSection, string> = {
  mes_ecritures: 'Mes écritures',
  pieces_adverses: 'Pièces adverses',
  nos_pieces: 'Mes pièces',
};

const LABELS_ADMIN: Record<DroitPublicSection, string> = {
  mes_ecritures: 'Mes mémoires',
  pieces_adverses: 'Productions du défendeur',
  nos_pieces: 'Mes pièces',
};

/**
 * Retourne le libellé de section adapté à la matière du dossier.
 *
 * @example
 *   getSectionLabel(adminCase, 'mes_ecritures')   // → 'Mes mémoires'
 *   getSectionLabel(civilCase, 'mes_ecritures')   // → 'Mes écritures'
 */
export function getSectionLabel(
  c: Pick<Case, 'procedure_type'> | null | undefined,
  section: DroitPublicSection,
): string {
  return isDroitPublic(c) ? LABELS_ADMIN[section] : LABELS_CIVIL[section];
}

/**
 * Libellé d'un emplacement de rangement **tel que l'avocat le lit à l'écran**,
 * matière comprise.
 *
 * Unique réponse à la question « comment s'appelle cet emplacement ? ». Deux
 * tables s'y rejoignent, et aucune ne suffit seule : `DOCUMENT_CATEGORIES`
 * porte le nom civil de chaque onglet, `LABELS_ADMIN` en renomme deux en
 * matière administrative. Noter que le nom civil vient bien du catalogue et
 * non de `LABELS_CIVIL` : l'onglet s'intitule « Partie adverse » quand la
 * table droit public dit « Pièces adverses ». Les deux sont justes dans leur
 * contexte — celle-ci est la seule à décrire l'ONGLET.
 *
 * Écrit le 2026-09-02 pour tuer une divergence, pas par goût de l'abstraction :
 * la modale d'enregistrement dans le dossier tenait sa propre copie des
 * libellés et proposait de ranger dans « Actes de procédure » ce que l'arborescence
 * appelle « Mes écritures », dans « Notes et projets » ce qu'elle appelle
 * « Notes client », et dans « Recherche » ce qu'elle appelle « Mes recherches
 * juridiques ». Quatre entrées sur six mentaient sur leur destination, et les
 * deux clés quasi-homographes `procedure` / `procedures` s'affichaient
 * « Mes écritures » / « Actes de procédure » — indiscernables au moment de choisir.
 *
 * Toute surface qui propose de ranger une pièce lit ICI. Une deuxième copie de
 * cette table est précisément ce qui a produit le bug.
 */
export function getCategoryTabLabel(
  c: Pick<Case, 'procedure_type'> | null | undefined,
  key: DocumentCategoryKey,
): string {
  if (isDroitPublic(c)) {
    if (key === 'procedure') return LABELS_ADMIN.mes_ecritures;
    if (key === 'partie_adverse') return LABELS_ADMIN.pieces_adverses;
  }
  return DOCUMENT_CATEGORIES[key].label;
}

// ---------------------------------------------------------------------------
// Party role labels
// ---------------------------------------------------------------------------

const PARTY_ROLE_CIVIL: Record<PartyRole, string> = {
  client: 'Notre client',
  opponent: 'Partie adverse',
  clients: 'Nos clients',
  opponents: 'Adverses',
};

const PARTY_ROLE_ADMIN: Record<PartyRole, string> = {
  // En matière administrative, la partie qui saisit le tribunal s'appelle le
  // « requérant » (terme canonique du contentieux administratif français —
  // CTA, CAA, CE). L'usage de « demandeur » est civil.
  client: 'Requérant',
  // La personne publique défenderesse est invariablement désignée
  // « l'Administration » dans la pratique contentieuse administrative. Le terme
  // « partie adverse » est employé en civil et sonne faux ici.
  opponent: 'Administration',
  clients: 'Requérants',
  opponents: 'Administration',
};

/**
 * Retourne le libellé de rôle de partie adapté à la matière du dossier.
 *
 * Accepte un `Case` (ou tout objet avec `procedure_type`) OU un `boolean`
 * `isDroitPublic` déjà calculé, pour s'aligner sur la signature de
 * {@link getSectionLabel} et éviter de recalculer le marqueur quand il est
 * déjà en scope.
 *
 * **Vocabulaire** :
 * - Civil  : `'client'` → « Notre client »  |  `'opponent'` → « Partie adverse »
 * - Admin  : `'client'` → « Requérant »     |  `'opponent'` → « Administration »
 *
 * « Partie » / « Parties » (générique neutre) ne sont pas couverts par ce
 * helper — ils restent identiques dans les deux matières.
 *
 * @example
 *   getPartyRoleLabel(adminCase, 'client')    // → 'Requérant'
 *   getPartyRoleLabel(true,      'opponents') // → 'Administration'
 *   getPartyRoleLabel(civilCase, 'opponent')  // → 'Partie adverse'
 */
export function getPartyRoleLabel(
  cOrFlag: Pick<Case, 'procedure_type'> | null | undefined | boolean,
  role: PartyRole,
): string {
  const isAdmin = typeof cOrFlag === 'boolean' ? cOrFlag : isDroitPublic(cOrFlag);
  return isAdmin ? PARTY_ROLE_ADMIN[role] : PARTY_ROLE_CIVIL[role];
}
